import { SHContainer, SHStack, SHTypography } from "@components/design-systems";
import { Collapse } from "@mui/material";
import { MSTableFilter } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter";
import { MSTableSearchBox } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/search-box";
import { MSDefaultDelayTimeMs } from "@pages/managed-accounts/model-screener/constant";
import {
  resetTemporaryData,
  setColumnSetting,
  setFilters,
  toggleFiltering,
} from "@redux/slices/model-screener";
import { RootState, useAppDispatch } from "@redux/store";
import { generateUUID } from "@utils";
import { isEqual } from "lodash";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useUnmount } from "react-use";
import { MSTableMonthRangePicker } from "src/pages/managed-accounts/model-screener/components/model-screener-table/toolbar/month-range-picker";
import { MSTableColumnSetting } from "./column-setting";
import { MSFilterDefaultValueConstants } from "./filter/constants";
import { MSTableResetButton } from "./reset-button";

export const MSTableToolBar = () => {
  const dispatch = useAppDispatch();
  // Store
  const shouldHideToolbar = useSelector(
    (state: RootState) =>
      state.modelScreener.tableScrollProps.shouldHideToolbar,
  );
  const numberOfFiltersApplied = useSelector(
    (state: RootState) => state.modelScreener.ui.numberOfFiltersApplied,
  );
  const numOfHiddenColumnApplied = useSelector(
    (state: RootState) => state.modelScreener.ui.numberOfColumnsApplied,
  );
  const defaultFilterSettingData = useSelector(
    (state: RootState) =>
      state.modelScreener.internal.currentEditingFilterDefaultData,
  );
  const defaultColumnSettingData = useSelector(
    (state: RootState) => state.modelScreener.internal.defaultColumnSettingData,
  );
  const currentColumnSettingData = useSelector(
    (state: RootState) =>
      state.modelScreener.internal.currentEditingColumnSetting,
  );
  const currentFilterData = useSelector(
    (state: RootState) => state.modelScreener.internal.currentEditingFilter,
  );
  const isMSFiltering = useSelector(
    (state: RootState) => state.modelScreener.ui.isFiltering,
  );

  // State
  const [key, setKey] = useState(generateUUID());

  const shouldShowResetAll = useMemo(() => {
    return Boolean(numberOfFiltersApplied) || Boolean(numOfHiddenColumnApplied);
  }, [numberOfFiltersApplied, numOfHiddenColumnApplied]);

  const handleResetAll = () => {
    if (!defaultFilterSettingData || !defaultColumnSettingData) return;

    if (numberOfFiltersApplied) {
      dispatch(setFilters({ mainFilter: defaultFilterSettingData }));
    }
    if (!isEqual(currentColumnSettingData, defaultColumnSettingData)) {
      dispatch(setColumnSetting({ columnSetting: defaultColumnSettingData }));
    }

    setKey(generateUUID());
    setTimeout(
      () => dispatch(toggleFiltering(false)),
      MSFilterDefaultValueConstants.StandardDelayTimeMs,
    );
  };

  useUnmount(() => {
    dispatch(resetTemporaryData());
  });

  return (
    <SHContainer maxWidth={"lg"} sx={{ px: { xs: "16px", lg: 0 } }}>
      <Collapse
        orientation={"vertical"}
        in={!shouldHideToolbar}
        timeout={MSDefaultDelayTimeMs}
      >
        <SHStack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <MSTableSearchBox />

          <SHStack direction={"row"} gap={2} alignItems={"center"}>
            <MSTableMonthRangePicker />
            <SHTypography fontSize={25} colorVariant={"disabled"}>
              |
            </SHTypography>
            <SHStack key={key} direction={"row"} gap={2} alignItems={"center"}>
              {currentFilterData && (
                <MSTableFilter
                  key={`toolbar-filter-${key}`}
                  currentFilterData={currentFilterData}
                  disabled={isMSFiltering}
                />
              )}
              {currentColumnSettingData && (
                <MSTableColumnSetting
                  key={`toolbar-column-${key}`}
                  currentColumnSettingData={currentColumnSettingData}
                  disabled={isMSFiltering}
                />
              )}

              {shouldShowResetAll && (
                <MSTableResetButton
                  onClick={() => {
                    dispatch(toggleFiltering(true));
                    setTimeout(() => handleResetAll(), 0);
                  }}
                  label={"Reset"}
                  height="38px"
                  fontSize="14px"
                />
              )}
            </SHStack>
          </SHStack>
        </SHStack>
      </Collapse>
    </SHContainer>
  );
};
